
import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../service/Api';

/**
 * Custom hook to convert external image URLs to blob URLs via backend proxy
 * Automatically handles CORS issues and cleanup
 * 
 * @param {string|string[]|null} imageUrls - Single URL, array of URLs, or null
 * @returns {Object} - { blobUrls, loading, error, convertImage }
 * 
 * @example
 * // Single image
 * const { blobUrls, loading } = useImageProxy('https://example.com/logo.png');
 * 
 * @example
 * // Multiple images
 * const { blobUrls, loading } = useImageProxy([
 *   'https://example.com/logo1.png',
 *   'https://example.com/logo2.png'
 * ]);
 * 
 * @example
 * // Manual conversion
 * const { blobUrls, convertImage } = useImageProxy(null);
 * const blobUrl = await convertImage('https://example.com/logo.png');
 */
export const useImageProxy = (imageUrls = null) => {
    const [blobUrls, setBlobUrls] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Convert a single image URL to blob URL
     */
    const convertImage = useCallback(async (imageUrl) => {
        if (!imageUrl) return null;
        
        try {
            const blobUrl = await apiService.imageUrlToBlobUrl(imageUrl);
            return blobUrl;
        } catch (err) {
            console.error('Error converting image:', err);
            return null;
        }
    }, []);

    /**
     * Convert multiple image URLs to blob URLs
     */
    const convertImages = useCallback(async (urls) => {
        if (!urls) return null;
        
        const urlArray = Array.isArray(urls) ? urls : [urls];
        const promises = urlArray.map(url => convertImage(url));
        const results = await Promise.all(promises);
        
        return Array.isArray(urls) ? results : results[0];
    }, [convertImage]);

    useEffect(() => {
        let isMounted = true;
        const blobUrlsToCleanup = [];

        const loadImages = async () => {
            if (!imageUrls) {
                setBlobUrls(null);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const results = await convertImages(imageUrls);
                
                if (isMounted) {
                    setBlobUrls(results);
                    // Track blob URLs for cleanup
                    const resultArray = Array.isArray(results) ? results : [results];
                    blobUrlsToCleanup.push(...resultArray.filter(Boolean));
                }
            } catch (err) {
                if (isMounted) {
                    setError(err.message);
                    console.error('Error loading images:', err);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        loadImages();

        // Cleanup blob URLs when component unmounts
        return () => {
            isMounted = false;
            blobUrlsToCleanup.forEach(blobUrl => {
                if (blobUrl) {
                    URL.revokeObjectURL(blobUrl);
                    console.log('🧹 Cleaned up blob URL:', blobUrl);
                }
            });
        };
    }, [imageUrls, convertImages]);

    return {
        blobUrls,      // The converted blob URL(s) - single string or array
        loading,       // Loading state
        error,         // Error message if any
        convertImage,  // Manual conversion function for single image
        convertImages  // Manual conversion function for multiple images
    };
};

/**
 * Hook for converting images with explicit state management
 * Useful when you need more control over when conversion happens
 * 
 * @example
 * const { convertAndSet, blobUrl, loading } = useImageProxyState();
 * 
 * useEffect(() => {
 *   if (imageUrl) {
 *     convertAndSet(imageUrl);
 *   }
 * }, [imageUrl]);
 */
export const useImageProxyState = () => {
    const [blobUrl, setBlobUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const convertAndSet = useCallback(async (imageUrl) => {
        if (!imageUrl) {
            setBlobUrl(null);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const result = await apiService.imageUrlToBlobUrl(imageUrl);
            setBlobUrl(result);
            return result;
        } catch (err) {
            setError(err.message);
            console.error('Error converting image:', err);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const reset = useCallback(() => {
        if (blobUrl) {
            URL.revokeObjectURL(blobUrl);
        }
        setBlobUrl(null);
        setError(null);
        setLoading(false);
    }, [blobUrl]);

    useEffect(() => {
        // Cleanup on unmount
        return () => {
            if (blobUrl) {
                URL.revokeObjectURL(blobUrl);
            }
        };
    }, [blobUrl]);

    return {
        blobUrl,
        loading,
        error,
        convertAndSet,
        reset
    };
};

export default useImageProxy;

